/**
 * Web Users Vuex module for Portal Application
 * 
 * @module WebUsers
 * @see
 * @author Bailey Kulman (Fresche Solutions)
 * @date June 2023
 */

import portalapi from "@services/portalapi";
import { resetStateData } from "@/utils/vuexStoreUtils";
import { getField, updateField } from "vuex-map-fields";

const DEFAULT_STATE = {
  webUsers: [],
  selectedUser: {},
  userRoles: [],
  allUsers: [],
};

const state = structuredClone(DEFAULT_STATE);

const getters = {
  getField,

  getWebUsers(st) {
    return st.webUsers;
  },

  getSelectedUser(st) {
    return st.selectedUser;
  },

  getUserRoles(st) {
    return st.userRoles;
  },

  getAllUsers(st) {
    return st.allUsers;
  },
};

const mutations = {
  updateField, // for vuex-map-fields

  async clearStateData(st) {
    st = resetStateData(st, DEFAULT_STATE);
  },

  setWebUsers(st, payload) {
    st.webUsers = payload;
  },

  setSelectedUser(st, payload) {
    st.selectedUser = payload;
  },

  setUserRoles(st, payload) {
    st.userRoles = payload;
  },

  setAllUsers(st, payload) {
    st.allUsers = payload;
  },
};

const actions = {
  // Get web users
  async fetchWebUsers({ commit }, payload) 
  {
    const requestPayload = {
      page: (payload && payload.page ? payload.page : 1),
      itemsPerPage: (payload && payload.itemsPerPage ? payload.itemsPerPage : 10),
      filters: (payload && payload.filters ? payload.filters : []),
      sortBy: (payload && payload.sortBy ? payload.sortBy : {column: "userId", state: 0}),
    };
    
    const response = await portalapi.moduleWebUsers.getWebUsers(requestPayload);

    if (response && response.data && response.data.data) {
      response.data.data = response.data.data.map(item => {
        return actions.formatUserDataFromApi(item);
      });

      commit("setWebUsers", response.data);
    }
  },


  // Get selected user
  async fetchSelectedUser({ commit }, payload) 
  {
    let response = await portalapi.moduleWebUsers.getSelectedUserData(payload);
    let responseData = {};
    
    // Check if the user was found
    if (!response) {
      responseData = {
        userFound: false,
      };
    } else {
      responseData = actions.formatUserDataFromApi(response.data);
      responseData.userFound = true;
    }

    commit("setSelectedUser", responseData);
  },


  // Update selected user
  async updateSelectedUser({ commit }, userData) 
  {
    var requestData = actions.formatUserDataForApi(userData);

    var response =  await portalapi.moduleWebUsers.updateWebUser(requestData);

    return response;
  },


  // Create web user
  async createWebUser({ commit }, userData) 
  {
    var requestData = actions.formatUserDataForApi(userData);

    var response = await portalapi.moduleWebUsers.createWebUser(requestData);

    return response;
  },


  // Delete web user
  async deleteWebUser({ commit }, userData) 
  {
    return await actions.deleteUser({ commit }, userData, false);
  },


  // Admin delete web user
  async adminDeleteWebUser({ commit }, userData) 
  {
    return await actions.deleteUser({ commit }, userData, true);
  },


  async deleteUser({ commit }, userData, isAdmin) {
    const requestData = {
      userId: userData.userId,
      storeId: userData.storeId,
    };

    const response = await portalapi.moduleWebUsers.deleteWebUser(requestData);

    if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK && response.data > 0) {
      if (isAdmin) {
        await actions.fetchAllUsers({ commit });
      } else {
        await actions.fetchWebUsers({ commit });
      }
    }

    return response;
  },

  
  // Get user roles
  async fetchUserRoles(context) 
  {
    let commit = context.commit;

    if (context.state.userRoles.length === 0) {
      const response = await portalapi.moduleWebUsers.getUserRoles();

      if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
        commit("setUserRoles", response.data);
      }
    }
  },


  // Fetch all users (Super/Unipharm Admin only)
  async fetchAllUsers({ commit }, payload) 
  {
    const requestPayload = {
      page: (payload && payload.page ? payload.page : 1),
      itemsPerPage: (payload && payload.itemsPerPage ? payload.itemsPerPage : 10),
      filters: (payload && payload.filters ? payload.filters : []),
      sortBy: (payload && payload.sortBy ? payload.sortBy : {column: "storeId", state: "asc"}),
    };
    
    const response = await portalapi.moduleAdmin.getAllUsersPage(requestPayload);

    if (response && response.data && response.data.data) {
      response.data.data = response.data.data.map(item => {
        return actions.formatUserDataFromApi(item);
      });

      commit("setAllUsers", response.data);
    }
  },


  // Reset given user's NCC (Super/Unipharm Admin only)
  async resetNarcoticCode({ commit }, data) 
  {
    return await portalapi.moduleAdmin.resetNarcoticCode(data);
  },


  // Toggle given user's Super Admin authority (Super Admin only)
  async toggleSuperAdminAuthority({ commit }, data) 
  {
    return await portalapi.moduleAdmin.toggleSuperAdminAuthority(data);
  },


  // Formats user data for API request
  formatUserDataForApi(userData) 
  {
    userData.email = userData.email1;
    userData.userRoles = userData.userRoles.join(" ");
    userData.isActive = userData.userStatus == "A" ? true : false;

    delete userData.email1;
    delete userData.email2;
    delete userData.userStatus;

    return {
      data: userData
    };
  },


  // Formats user data from API response
  formatUserDataFromApi(userData) 
  {
    userData.email1 = userData.email;
    userData.email2 = userData.email;
    userData.userRoles = userData.userRoles.split(" ");
    userData.userStatus = userData.isActive ? "A" : "D";
    userData.passwordExpiryDate = vm.config.globalProperties.$filters.date(userData.passwordExpiryDate);

    return userData;
  },
};

export default {
  namespaced: "true",
  state,
  getters,
  mutations,
  actions,
};